import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SearchIcon, XIcon, InstagramIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { useAccessibility } from '../contexts/AccessibilityContext';
export function Navbar() {
  const {
    language,
    setLanguage,
    t,
    isRTL
  } = useLanguage();
  const {
    darkMode,
    setDarkMode,
    highContrast,
    setHighContrast,
    bigText,
    setBigText
  } = useAccessibility();
  const [searchOpen, setSearchOpen] = useState(false);
  const [a11yOpen, setA11yOpen] = useState(false);
  const a11yRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (a11yRef.current && !a11yRef.current.contains(event.target as Node)) {
        setA11yOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const location = useLocation();
  const navLinks = [{
    path: '/',
    label: t.nav.issues
  }, {
    path: '/about',
    label: t.nav.about
  }, {
    path: '/submit',
    label: t.nav.submit
  }, {
    path: '/contact',
    label: t.nav.contact
  }];
  const isActive = (path: string) => location.pathname === path;
  return <>
      <nav className="text-white sticky top-0 z-50 shadow-lg bg-[#E6B259]" dir="ltr" data-id="element-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-2 md:py-4" data-id="element-21">
          {/* Top row: Instagram + A11y | Logo | Search + Language */}
          <div className="flex items-center justify-between" data-id="element-22">
            {/* Left: Instagram + Search */}
            <div className="flex items-center gap-3 md:gap-4 w-20 md:w-32" data-id="element-23">
              <a href="https://www.instagram.com/reference.journal" target="_blank" rel="noopener noreferrer" className="hover:text-amber-200 transition-colors" aria-label="Instagram" data-id="element-24">
                <InstagramIcon size={22} data-id="element-25" />
              </a>
              <button onClick={() => setSearchOpen(!searchOpen)} className="hover:text-amber-200 transition-colors" aria-label="Search" data-id="element-26">
                <SearchIcon size={20} data-id="element-27" />
              </button>
            </div>

            {/* Center: Logo */}
            <Link to="/" className="flex-shrink-0" data-id="element-28">
              <img src="https://cdn.magicpatterns.com/uploads/4tYRvAz4Dc4AAEu6h2rfrm/Transparent_Background_Logo.png" alt="Reference Journal" className="h-16 md:h-24 w-auto" data-id="element-29" />
            </Link>

            {/* Right: A11y + Language Toggle */}
            <div className="flex items-center justify-end gap-3 md:gap-6 w-28 md:w-40" data-id="element-30">
              <div className="relative space-y-[6px]" ref={a11yRef} data-id="element-31">
                <button onClick={() => setA11yOpen(!a11yOpen)} className="hover:text-amber-200 transition-colors" aria-label="Accessibility Menu" data-id="element-32">
                  <img src="https://cdn.magicpatterns.com/uploads/bXk6paqQhWF9Mh4yfN5UFa/accessibility.png" alt="Accessibility" className="w-[22px] h-[22px] brightness-0 invert" data-id="element-33" />
                </button>

                <AnimatePresence data-id="element-34">
                  {a11yOpen && <motion.div initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: 10
                }} className="absolute top-full right-0 mt-4 w-48 bg-white rounded-lg shadow-xl p-4 text-[#1B2A4A] z-50 border border-gray-100" data-id="element-35">
                      <h3 className="font-bold text-sm mb-3 border-b border-gray-100 pb-2" data-id="element-36">
                        {language === 'he' ? 'נגישות' : 'Accessibility'}
                      </h3>

                      <div className="space-y-3" data-id="element-37">
                        <label className="flex items-center justify-between cursor-pointer group" data-id="element-38">
                          <span className="text-sm font-medium group-hover:text-amber-600 transition-colors" data-id="element-39">
                            {language === 'he' ? 'מצב כהה' : 'Dark Mode'}
                          </span>
                          <div className={`w-8 h-4 rounded-full transition-colors relative ${darkMode ? 'bg-[#2A4B7C]' : 'bg-gray-300'}`} data-id="element-40">
                            <div className={`absolute top-0.5 left-0.5 w-3 h-3 rounded-full bg-white transition-transform ${darkMode ? 'translate-x-4' : 'translate-x-0'}`} data-id="element-41" />
                          </div>
                          <input type="checkbox" className="hidden" checked={darkMode} onChange={() => setDarkMode(!darkMode)} data-id="element-42" />
                        </label>

                        <label className="flex items-center justify-between cursor-pointer group" data-id="element-43">
                          <span className="text-sm font-medium group-hover:text-amber-600 transition-colors" data-id="element-44">
                            {language === 'he' ? 'ניגודיות גבוהה' : 'High Contrast'}
                          </span>
                          <div className={`w-8 h-4 rounded-full transition-colors relative ${highContrast ? 'bg-[#2A4B7C]' : 'bg-gray-300'}`} data-id="element-45">
                            <div className={`absolute top-0.5 left-0.5 w-3 h-3 rounded-full bg-white transition-transform ${highContrast ? 'translate-x-4' : 'translate-x-0'}`} data-id="element-46" />
                          </div>
                          <input type="checkbox" className="hidden" checked={highContrast} onChange={() => setHighContrast(!highContrast)} data-id="element-47" />
                        </label>

                        <label className="flex items-center justify-between cursor-pointer group" data-id="element-48">
                          <span className="text-sm font-medium group-hover:text-amber-600 transition-colors" data-id="element-49">
                            {language === 'he' ? 'טקסט גדול' : 'Big Text'}
                          </span>
                          <div className={`w-8 h-4 rounded-full transition-colors relative ${bigText ? 'bg-[#2A4B7C]' : 'bg-gray-300'}`} data-id="element-50">
                            <div className={`absolute top-0.5 left-0.5 w-3 h-3 rounded-full bg-white transition-transform ${bigText ? 'translate-x-4' : 'translate-x-0'}`} data-id="element-51" />
                          </div>
                          <input type="checkbox" className="hidden" checked={bigText} onChange={() => setBigText(!bigText)} data-id="element-52" />
                        </label>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>

              <div className="flex items-center gap-1.5 md:gap-2 text-sm" data-id="element-53">
                <button onClick={() => setLanguage('en')} className={`transition-colors ${language === 'en' ? 'text-[#2A4B7C] font-bold' : 'text-white hover:text-white/80 font-medium'}`} data-id="element-54">
                  EN
                </button>
                <span className="text-white/40" data-id="element-55">|</span>
                <button onClick={() => setLanguage('he')} className={`transition-colors ${language === 'he' ? 'text-[#2A4B7C] font-bold' : 'text-white hover:text-white/80 font-medium'}`} data-id="element-56">
                  עב
                </button>
              </div>
            </div>
          </div>

          {/* Bottom row: Centered nav links */}
          <div className="flex items-center justify-center gap-4 md:gap-8 mt-0 md:mt-1 pt-1 md:pt-2 border-t border-white/10 flex-wrap" data-id="element-57">
            {navLinks.map(link => <Link key={link.path} to={link.path} className={`text-sm ${isActive(link.path) ? 'text-[#2A4B7C] font-bold border-b-2 border-[#2A4B7C] pb-1' : 'text-white font-medium hover:text-white/80 transition-colors'}`} data-id="element-58">
                {link.label}
              </Link>)}
          </div>
        </div>
      </nav>

      {/* Search Overlay */}
      <AnimatePresence data-id="element-59">
        {searchOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-start justify-center pt-32" onClick={() => setSearchOpen(false)} data-id="element-60">
            <motion.div initial={{
          y: -20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: -20,
          opacity: 0
        }} className="bg-white rounded-lg shadow-2xl w-full max-w-2xl mx-4" onClick={e => e.stopPropagation()} data-id="element-61">
              <div className="p-6" data-id="element-62">
                <div className="flex items-center gap-4 mb-4" data-id="element-63">
                  <SearchIcon size={24} className="text-gray-400" data-id="element-64" />
                  <input type="text" placeholder="Search articles, issues..." className="flex-1 text-lg outline-none" autoFocus data-id="element-65" />
                  <button onClick={() => setSearchOpen(false)} className="text-gray-400 hover:text-gray-600" data-id="element-66">
                    <XIcon size={24} data-id="element-67" />
                  </button>
                </div>
                <div className="text-sm text-gray-500" data-id="element-68">
                  Search functionality coming soon...
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}